import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Subject, Subscription, debounceTime } from 'rxjs';
import { RouterLink } from '@angular/router';
import { PageHeader } from '../../../shared/page-header/page-header';
import { Pagination } from '../../../shared/pagination/pagination';
import { AuthService } from '../../../core/auth/auth.service';
import { ClientService } from '../../../core/client/client.service';
import { Client, ClientStatistics, ClientStatus } from '../../../core/client/client.models';
import { STATUS_LABELS, STATUS_OPTIONS, statusBadge } from '../../../core/client/client.labels';

interface Tile {
  label: string;
  value: number;
  icon: string;
  tone: 'primary' | 'success' | 'warning';
}

@Component({
  selector: 'app-client-list',
  imports: [RouterLink, FormsModule, DatePipe, PageHeader, Pagination],
  templateUrl: './client-list.html',
})
export class ClientList implements OnInit {
  private readonly service = inject(ClientService);
  protected readonly auth = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly search$ = new Subject<void>();
  private fetching?: Subscription;

  protected readonly clients = signal<Client[]>([]);
  protected readonly stats = signal<ClientStatistics | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  protected readonly statusLabels = STATUS_LABELS;
  protected readonly statusOptions = STATUS_OPTIONS;
  protected readonly statusBadge = statusBadge;

  protected status: ClientStatus | null = null;
  protected q = '';

  protected readonly pageSize = 10;
  protected readonly page = signal(1);
  protected readonly total = signal(0);

  protected readonly tiles = computed<Tile[]>(() => {
    const s = this.stats();
    if (!s) return [];
    return [
      { label: 'Clientes', value: s.total, icon: 'fe-users', tone: 'primary' },
      { label: 'Ativos', value: s.active, icon: 'fe-user-check', tone: 'success' },
      { label: 'Inativos', value: s.inactive, icon: 'fe-user-x', tone: 'warning' },
    ];
  });

  ngOnInit(): void {
    this.search$.pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef)).subscribe(() => this.load());
    this.service.statistics().subscribe({ next: (s) => this.stats.set(s) });
    this.load();
  }

  /** A escrita espera 300 ms e o pedido anterior é cancelado: nada de um pedido por tecla. */
  protected onSearch(): void {
    this.search$.next();
  }

  protected load(): void {
    this.page.set(1);
    this.fetch();
  }

  protected onPage(p: number): void {
    this.page.set(p);
    this.fetch();
  }

  private fetch(): void {
    this.loading.set(true);
    this.error.set(null);
    this.fetching?.unsubscribe();
    this.fetching = this.service.list({ status: this.status, q: this.q, page: this.page() - 1, size: this.pageSize }).subscribe({
      next: (res) => {
        this.clients.set(res.content);
        this.total.set(res.totalElements);
        this.loading.set(false);
      },
      error: () => {
        this.error.set('Não foi possível carregar os clientes.');
        this.loading.set(false);
      },
    });
  }
}
